import dayjs from 'dayjs';
import { v4 } from 'uuid';
import {
  // formatTaskCategoryName,
  TASK_EVIDENCE_TYPE,
  TASK_STATUS,
  TASK_TYPE,
} from 'utils/constants/task';
import { transformEvidenceInitialValue } from 'utils/DataTransform/Task/taskForm';
import { mapIconSelector, TASK_FIELDS } from 'utils/formFields/task';
import { getTaskLocationMarker } from 'utils/helper/task';
import { patrolFormFields } from 'utils/patrol';
import { getTimeInMins } from 'utils/time';
import { TaskInfo, SiteInfo } from 'utils/constants/informationText';
import {
  convertArrayStrToObject,
  optionalFormField,
} from 'utils/methods/helpers';
import MultiSchedule from './MultiSchedule';
import {
  MULTI_SCHEDULE_COLUMNS,
  transformTableMultiSchedule,
} from './MultiSchedule/options';
import { transformScheduleByPattern } from 'utils/DataTransform/Task/schedule';
import { SCHEDULE_PATTERNS } from 'utils/constants/schedule';
import { GEOGRAPHICAL_MAP } from 'utils/constants/Map';
// import { setSiteName } from 'utils/helper/site';
import { DB_NULL } from '../../../utils/constants/index';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import timezone from 'dayjs/plugin/timezone';
import { USER_FIELDS } from 'utils/formFields/user';
import { getItemOrDefaultToValue } from 'utils/details';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { severityRatingOptions } from 'containers/Pages/Events/Layouts';
import { EventPlaceholder } from 'utils/constants/eventStatus';
import SiteForm from 'containers/Pages/Locations/Sites/Layouts/baseSiteForm';
import API from 'services/api';
import { transform as siteListTransform } from 'utils/DataTransform/Sites/SitesList';
import { setSiteName } from 'utils/helper/site';
import { serviceTypes } from 'utils/constants/serviceTypes';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(timezone);

export const defaultValues = {
  taskType: TASK_TYPE.CLEANING.id,
  isTemplate: false,
  wrapsId: null,
  scanCode: null,
  scanAutoStart: null,
  scanStartOnly: null,
  surfaceViewPreferred: true,
  status: TASK_STATUS.NEW.id,
};

export const getTaskLocation = ({
  surfaceV,
  surfaceU,
  taskType,
  subtasksSequentialOrder,
  surfaceId,
  locationId,
}) => {
  if (surfaceV && surfaceU) {
    const location = [
      getTaskLocationMarker({
        lat: surfaceV,
        lng: surfaceU,
        icon: taskIconSelector(taskType),
        text: 1,
        seqNumber: 0,
        id: locationId,
        showPolylines: !subtasksSequentialOrder
          ? false
          : subtasksSequentialOrder,
      }),
    ];
    const locationDetail = { center: { lat: surfaceV, lng: surfaceU } };

    return {
      location,
      ...(surfaceId === GEOGRAPHICAL_MAP.id && locationDetail), // location details is only required when surface is google maps
    };
  }

  return { location: [] };
};
export const taskIconSelector = taskType => {
  switch (taskType?.category) {
    case serviceTypes.maintenance:
      return TASK_TYPE.MAINTENANCE.mapIcon;
    case serviceTypes.cleaning:
      return TASK_TYPE.CLEANING.mapIcon;
    default:
      return '-';
  }
};
const transformTaskScheduleInitial = ({ task, timezone }) => {
  const activeTab = task.schedule ? SCHEDULE_PATTERNS.onceOff.label : '';
  return {
    schedule: activeTab
      ? {
          activeTab: SCHEDULE_PATTERNS.onceOff.label,
          onceOff: {
            id: task.schedule.id || undefined,
            finishBefore: task.schedule.finishBefore
              ? dayjs(task.schedule.finishBefore).tz(timezone)
              : null,
            startAfter: task.schedule.startAfter
              ? dayjs(task.schedule.startAfter).tz(timezone)
              : null,
            timezone,
          },
        }
      : null,
    addSchedule: Boolean(activeTab),
  };
};

export const transformMultiScheduleInitial = ({
  values = [],
  sites = [],
  users = {},
}) => {
  if (!Array.isArray(values)) return values;
  const schedulePatterns = values.map(item => {
    const { assignToUserIDs, siteId, scheduleType } = item;

    const assignees = assignToUserIDs.reduce(
      (acc, id) => (!users[id] ? acc : [...acc, { id, name: users[id] }]),
      [],
    );

    const editableValues = {
      id: item.id,
      scheduleId: v4(),
      assignToUserIDs: assignees,
      schedule: transformScheduleByPattern({ scheduleType, data: item }),
      site: siteId ? sites.find(s => s.id === siteId) || '' : '',
      scheduleType: item.scheduleType,
    };

    return {
      ...editableValues,
      originalData: { id: item.id, ...editableValues },
    };
  });

  return { schedulePatterns };
};
const getImageDimensions = (surfaceId, surfaces) => {
  // find the requiredSurfaceId from surfaces
  const requiredSurfaceId = surfaces.find(s => s.id === surfaceId);
  if (requiredSurfaceId) {
    const { height, width } = requiredSurfaceId;
    return {
      width: width,
      height: height,
    };
  }
  return null;
};

export const getInitialValues = ({
  id,
  values,
  defaultValues = {},
  subTaskDefaultValues = {},
  // taskType,
  allowSubTask,
  templateId,
  isTemplate,
  multiSchedule,
  sites,
  users = {},
  timezone,
  globalDepartmentList = [],
  siteList = [],
  locationId,
}) => {
  // root task
  let tasks = values?.tasks.find(item => item.id === id);
  // if departments is already assigned no need to add it
  if (!tasks?.departments) {
    // selected task assigned departments
    const selectedDepartments = globalDepartmentList.filter(f =>
      getItemOrDefaultToValue(tasks, 'departmentIds', []).includes(f.id),
    );
    // This will give the assigned departments for the linked site
    const assignedDepartmentsSites =
      siteList.find(f => f.id === tasks?.site.id)?.assignedDepartmentList || [];

    const siteWithAssignedDepartment = {
      id: get(tasks, 'site.id', ''),
      name: get(tasks, 'site.name', ''),
      assignedDepartmentList: tasks?.site?.id ? assignedDepartmentsSites : [],
    };
    // add selectedDepartments and associated sites to the task object
    if (tasks) {
      tasks = {
        ...tasks,
        departments: selectedDepartments,
        site: { ...tasks.site, ...siteWithAssignedDepartment },
      };
    }
  }
  const surfaces = values?.surfaces;
  if (!tasks) {
    return defaultValues;
  }

  const {
    evidence = [],
    surfaceV,
    surfaceU,
    descriptionImageUrl,
    description,
    businessClientId,
    siteId,
    name,
    site,
    businessClient,
    subtasksSequentialOrder,
    expectedDuration,
    schedule,
    assigneeFullName,
    assigneeId,
    surfaceId,
    assetId,
    asset,
    ...rest
  } = tasks;
  /*eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
  const transformEvidence = transformEvidenceInitialValue({
    evidence,
  });
  const checkListEvidence = transformEvidence.filter(
    f => f.type.value === TASK_EVIDENCE_TYPE.CHECKBOX.id,
  );
  const evidenceWithoutCheckbox = transformEvidence.filter(
    f => f.type.value !== TASK_EVIDENCE_TYPE.CHECKBOX.id,
  );
  const taskTypeObject = {
    id: tasks.taskTypeID,
    name: tasks.taskType,
    category: tasks.taskCategory,
    categoryIcon: tasks.categoryIcon,
  };

  // if the parent surface Id matches the surface id of the sub task then add the location of the sub task to the
  // additionalMarker array of the parent
  const initialValues = {
    ...defaultValues,
    ...rest,
    taskName: name,
    checkList: checkListEvidence,
    taskDescription: description || undefined,
    evidence: evidenceWithoutCheckbox,
    taskImage: descriptionImageUrl,
    businessClient: businessClientId,
    ...(assetId && {
      assetId: { id: asset.id, name: asset.name },
    }),
    subtasksSequentialOrder,
    ...(assigneeId !== DB_NULL &&
      assigneeId !== null && {
        assigneeId: { id: assigneeId, name: assigneeFullName },
      }),
    site: site,
    ...(expectedDuration && {
      expectedDuration: getTimeInMins(expectedDuration),
    }),
    ...(surfaceId !== null &&
      surfaces.length > 0 && {
        imageDimensions: getImageDimensions(surfaceId, surfaces),
      }),
    siteSurfaceImages:
      surfaceId === null || surfaceId === DB_NULL
        ? GEOGRAPHICAL_MAP
        : values.surfaces.filter(f => f.id === surfaceId)[0],
    ...(typeof isTemplate !== 'undefined' && { isTemplate }),
    ...getTaskLocation({
      surfaceV,
      surfaceU,
      taskType: taskTypeObject, // the parentTaskType is should replace the taskType of copied task
      surfaceId,
      locationId,
    }),
    taskType: taskTypeObject,
    ...(templateId && { templateId }),
    ...(!multiSchedule && {
      ...transformTaskScheduleInitial({
        task: tasks,
        multiSchedule,
        timezone,
      }),
    }),
    ...(multiSchedule && {
      ...transformMultiScheduleInitial({
        values: tasks.schedulePatterns,
        sites,
        users,
      }),
    }),
  };
  const subtasks = getJoinInitialValues({
    parentId: id,
    allowSubTask,
    data: values,
    subTaskDefaultValues,
    parentTaskType: defaultValues.taskType,
    locationId,
  });

  return { ...initialValues, ...(subtasks && { ...subtasks }) };
};

export const getJoinInitialValues = ({
  allowSubTask,
  data,
  parentId,
  subTaskDefaultValues,
  parentTaskType,
}) => {
  if (!allowSubTask) {
    return undefined;
  }

  const joins = data.joins.filter(item => item.taskId === parentId);

  if (joins.length < 1) {
    return [];
  }

  const subtasksLocation = [];
  // arrange subtasks according to joins sequence
  const arrangedSubTasks = [];
  joins.forEach(item => {
    const subtask = getInitialValues({
      id: item.subtaskId,
      values: data,
      defaultValues: subTaskDefaultValues,
      taskType: parentTaskType ? parentTaskType : '',
      allowSubTask,
      templateId: item.templateId,
    });

    if (subtask.location && subtask.location.length > 0) {
      subtasksLocation[item.sequence - 1] = getTaskLocationMarker({
        id: subtask.location[0].id,
        lat: subtask.location[0].lat,
        lng: subtask.location[0].lng,
        icon: mapIconSelector(parentTaskType),
        showPolylines:
          subtask.subtasksSequentialOrder === null
            ? false
            : subtask.subtasksSequentialOrder,
        text: item.sequence,
        seqNumber: item.sequence,
        surfaceUuid: subtask?.siteSurfaceImages?.id,
        subtaskId: item.subtaskId,
      });
    }
    arrangedSubTasks[item.sequence - 1] = subtask;
  });
  return {
    subtasks: arrangedSubTasks,
    additionalMarkers: subtasksLocation,
  };
};

export const generalSection = ({
  hideAssignee,
  omitFields = [],
  popUpRequired,
  companyId,
  userAssignedDepartments,
  siteEnabled,
  services = [],
  view,
  updatedValues,
  selectedDepartments,
  disabledFields = [],
  selectedTaskType,
}) => {
  const fieldsToOmit = convertArrayStrToObject({ array: omitFields });
  return [
    TASK_FIELDS.name({ size: 'full' }),
    TASK_FIELDS.taskType({
      archived: false,
      services,
      view,
      size: 'half',
      required: true,
      updateDependentField: [
        // if this component is updated, always clear assignee
        {
          key: 'assigneeId',
          passValue: '', // assign this value
          condition: value => value, // always true, therefore, it will ensure that assigneeId will be deselected when taskType is changed
        },
        {
          key: 'schedulePatterns',
          passValue: '', // assign this value
          // eslint-disable-next-line no-unused-vars
          condition: (_, val) => {
            if (!val.schedulePatterns) return false;
            val.schedulePatterns = val.schedulePatterns.map(sp => {
              if (!isEmpty(sp.assignToUserIDs)) {
                return {
                  ...sp,
                  assignToUserIDs: [],
                };
              }
              return sp;
            });
            return false;
          }, // always false, therefore, it will ensure that schedule patterns just change the assigneeId if the taskType has changed and not the whole schedule
        },
      ],
    }),
    ...optionalFormField({
      showWhen: !hideAssignee,
      field: TASK_FIELDS.assignee({
        size: 'half',
        selectedTaskType,
      }),
    }),
    ...optionalFormField({
      showWhen: userAssignedDepartments.length > 0,
      field: USER_FIELDS.departments({
        userAssignedDepartments,
        disabled: disabledFields.includes('departments'),
      }),
    }),

    {
      field: 'severityRating',
      type: 'select',
      label: 'Severity Rating',
      options: severityRatingOptions,
      size: 'half',
    },
    {
      field: 'severity',
      type: 'text',
      label: 'Severity Description',
      maxLength: 50,
      placeholder: EventPlaceholder.severity,
    },
    ...optionalFormField({
      showWhen: siteEnabled,
      field: {
        field: 'site',
        type: 'async-select',
        info: SiteInfo.businessClient,
        required: false,
        size: 'half',
        valueType: 'object',
        disabled: disabledFields.includes('site') || false,
        fetchOptions: API.fetchSitesList,
        fetchFilters: {
          securityCompanyId: companyId,
          archived: false,
          ...(!isEmpty(selectedDepartments) && {
            departmentId: selectedDepartments
              .map(m => ({ id: m.id }))
              .map(m => m.id),
          }),
        },
        optionLabelKey: 'name',
        resetWhen: [
          {
            value: selectedDepartments,
            condition: val => {
              return !isEmpty(val);
            },
          },
        ],
        dataTransform: ({ data }) => {
          const transformedNameSites = data.map(site => ({
            ...site,
            id: site.id,
            name: setSiteName(site),
          }));
          return siteListTransform(
            transformedNameSites,
            userAssignedDepartments,
          );
        },
      },
    }),
    TASK_FIELDS.linkAssetId({
      securityCompanyId: companyId,
      archived: false,
      size: 'half',
      disabled: disabledFields.includes('assetId'), // assetId is the field of this component
    }),
    ...optionalFormField({
      showWhen: !fieldsToOmit.newSite,
      field: {
        field: 'site', // this field name should be same as the one you are trying to replace value with
        type: 'embedded-form-follow-up',
        label: 'New Site',
        component: SiteForm,
        archiveDelete: true,
        updatedValues,
        hideTable: true,
        popUpRequired, // condition when popup should be activated
        setKeyValues: {
          checkpoints: [],
          site: {},
          siteSurfaceImages: GEOGRAPHICAL_MAP,
          locationDetail: [],
        }, // key values to set if pressed ok
        componentProps: {
          embeddedForm: true,
          disablePresist: true,
        },
      },
    }),
  ];
};

export const detailedSection = ({
  siteLocation,
  users,
  columns,
  showPolylines,
  taskDetails,
  center,
  subtaskDefaultValues,
  isRootTask,
  scheduleActiveTab,
  schedulingView,
  hideAssignee,
  omitFields = [],
  multiSchedule,
  subtaskOmitFields,
  editMode,
  selectedSiteDetailImages,
  surfaceImage,
  location = [],
  selectedTaskType,
  siteEnabled,
}) => {
  const fieldsToOmit = convertArrayStrToObject({ array: omitFields });
  return [
    TASK_FIELDS.description(),
    TASK_FIELDS.checkListRequired(),
    TASK_FIELDS.evidenceRequired(),
    ...optionalFormField({
      showWhen: isRootTask && selectedTaskType && multiSchedule,
      field: {
        field: 'schedulePatterns',
        type: 'embedded-form',
        component: MultiSchedule,
        label: 'Schedule',
        columns: MULTI_SCHEDULE_COLUMNS,
        allowEditRow: false,
        dataTransform: data => transformTableMultiSchedule(data),
        archiveDelete: true,
        deleteReferenceKey: 'scheduleId',
      },
    }),
    ...optionalFormField({
      showWhen: !fieldsToOmit.generateQRCode,
      field: TASK_FIELDS.generateQR({ required: true }),
    }),
    ...optionalFormField({
      showWhen: isRootTask && !multiSchedule,
      field: TASK_FIELDS.schedule({
        activeTab: scheduleActiveTab,
        displayView: schedulingView,
        editMode,
      }),
    }),
    ...optionalFormField({
      showWhen: isRootTask,
      field: TASK_FIELDS.expectedDuration({ required: true }),
    }),

    ...optionalFormField({
      showWhen: false,
      field: TASK_FIELDS.subtasks({
        users,
        columns,
        defaultValues: {
          ...subtaskDefaultValues,
          taskType: selectedTaskType,
        },
        taskDetails,
        hideAssignee,
        omitFields: subtaskOmitFields,
        multiSchedule,
        surfaceImage,
        allowDrag: true, // this allows the rows to be dragged and reordered
        info: TaskInfo.reorder,
        siteEnabled,
      }),
    }),
    ...optionalFormField({
      showWhen: !isEmpty(selectedTaskType),
      field: patrolFormFields.siteSurfaceImages({
        options: selectedSiteDetailImages,
        useSelectedValue: true,
        valueType: 'object',
        defaultValue: GEOGRAPHICAL_MAP,
        showPopup: true,
        info: TaskInfo.siteSurfaceImages,
        surfaceTouched: location.length > 0,
        handleSelectChange: ({ e, handleChange, value, name }) => {
          e.target.value = value;
          e.target.name = name;
          handleChange(e);
        },
      }),
    }),
    ...optionalFormField({
      showWhen:
        surfaceImage.id === GEOGRAPHICAL_MAP.id && !isEmpty(selectedTaskType),
      field: TASK_FIELDS.location({
        siteLocation,
        showPolylines,
        center,
        // singleMarkerIcon: mapIconSelector(selectedTaskType), // pass this or typeOfIcon
        singleMarkerIcon: taskIconSelector(selectedTaskType), // pass this or typeOfIcon
        // typeOfIcon: 'CHECKPOINT', // type of icon for first point
        displaySites: true, // displays all the sites on map
      }),
    }),

    ...optionalFormField({
      showWhen:
        surfaceImage.id !== GEOGRAPHICAL_MAP.id && !isEmpty(selectedTaskType),
      field: TASK_FIELDS.imageArtist({
        showPolylines,
        selectedSiteDetailImages: surfaceImage,
        singleMarkerIcon: taskIconSelector(selectedTaskType),
        // singleMarkerIcon: mapIconSelector(selectedTaskType),
        // typeOfIcon: 'CHECKPOINT', // type of icon for first point
      }),
    }),
  ];
};

export const resetClonedTaskValues = ({ values }) => {
  return {
    ...values,
    id: v4(),
    templateId: values.id,
    evidence: (values.evidence || []).map(({ type, description, ...rest }) => ({
      type,
      description,
      ...rest,
    })),
    status: TASK_STATUS.NEW.id,
    assigneeFullName: null,
    addSchedule: false,
    schedule: null,
    schedulePatterns: [],
    assigneeId: null,
    templateValues: {
      finishedByFullName: null,
      finishedByID: null,
      startedByFullName: null,
      startedByID: null,
    },
  };
};

export const cloneTaskInitialValues = ({
  taskToCloneId,
  taskToCloneDetails,
  defaultValues,
  subTaskDefaultValues,
  allowSubTask,
  globalDepartmentList = [],
  siteList = [],
  locationId,
}) => {
  const clonedTask = getInitialValues({
    id: taskToCloneId,
    values: taskToCloneDetails,
    defaultValues,
    subTaskDefaultValues,
    allowSubTask,
    isTemplate: defaultValues.isTemplate,
    globalDepartmentList,
    siteList,
    locationId,
  });
  const reformCloneTask = resetClonedTaskValues({ values: clonedTask });
  // This will change the id of sub tasks to new uuid
  if (allowSubTask && reformCloneTask.subtasks) {
    reformCloneTask.subtasks = reformCloneTask.subtasks.map(item => {
      // if the sub task has a location then preserve it otherwise undefined
      const locationId = !isEmpty(item.location) && item.location[0].id;
      const taskItem = cloneTaskInitialValues({
        taskToCloneId: item.id,
        taskToCloneDetails,
        defaultValues,
        subTaskDefaultValues,
        allowSubTask,
        // do not change the id of locations of sub tasks and additional markers
        locationId,
      });

      const join = taskToCloneDetails.joins.find(j => j.subtaskId === item.id);

      return {
        ...taskItem,
        joinTemplateId: join ? join.id : null,
      };
    });
  }
  return reformCloneTask;
};
